
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { TenderAPI } from "../Services/Api";
import { useSession } from "../Contexts/SessionContext";

type Tender = {
  tenderNo: number;
  tenderName: string | null;
  tenderType?: string | null;
  contractDate?: string | null;
  project?: number | null;
};

const HEADER_COLORS = [
  "#78a5bd",
  "#78889f",
  "#929292",
  "#697b91",
];

const ProjectContracts: React.FC = () => {
  // =========================================================
  // PROJECT INFORMATION
  // =========================================================

  const { projectNo: projectNoParam } = useParams<{
    projectNo: string;
  }>();

  const navigate = useNavigate();
  const projectNo = Number(projectNoParam);

  // =========================================================
  // SESSION INFORMATION
  // =========================================================

  const {
    sessionNo,
    projectNo: activeProjectNo,
  } = useSession();

  // =========================================================
  // STATE
  // =========================================================

  const [contracts, setContracts] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =========================================================
  // LOAD CONTRACTS FOR THE SELECTED PROJECT
  // =========================================================

  const loadContracts = useCallback(async () => {
    if (!Number.isInteger(projectNo) || projectNo <= 0) {
      setError("Invalid project number.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = await TenderAPI.getByProject(projectNo);

      setContracts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load contracts:", err);

      setError("Failed to load contracts for this project.");
    } finally {
      setLoading(false);
    }
  }, [projectNo]);

  useEffect(() => {
    void loadContracts();
  }, [loadContracts]);

  // =========================================================
  // CREATE CONTRACT
  // =========================================================

  const createContract = () => {
    if (sessionNo == null || activeProjectNo !== projectNo) {
      setError(
        "Session not found for this project. Please return to Dashboard and select the project again."
      );

      return;
    }

    navigate(
      `/create-tender?projectNo=${projectNo}&sessionNo=${sessionNo}`
    );
  };

  // =========================================================
  // MAIN PAGE
  // =========================================================

  return (
    <div
      className="h-full overflow-y-auto bg-[#f4f5f7]"
      style={{
        fontFamily: '"Segoe UI", sans-serif',
        fontSize: "13px",
        fontWeight: 400,
      }}
    >
      <div className="w-full px-4 py-5 sm:px-5">

        {/* =====================================================
            CONTRACTS HEADING AND CREATE BUTTON
            ===================================================== */}

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">

          {/* LEFT SIDE - EXISTING CONTRACTS */}

          <div className="flex items-center gap-4">
            <h3
              className="text-[#243b53]"
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              Existing Contracts
            </h3>

            {!loading && !error && (
              <span
                className="text-gray-500"
                style={{
                  fontSize: "13px",
                  fontWeight: 400,
                }}
              >
                Total: {contracts.length}
              </span>
            )}
          </div>

          {/* RIGHT SIDE - CREATE CONTRACT */}

         {/*  <button
            type="button"
            onClick={createContract}
            className="
              rounded-md
              bg-[#53538c]
              px-4
              py-2
              text-white
              transition
              duration-200
              hover:bg-[#424270]
              hover:shadow-md
              focus:outline-none
              focus:ring-2
              focus:ring-[#53538c]
              focus:ring-offset-2a
            "
            style={{
              fontSize: "13px",
              fontWeight: 400,
            }}
          >
             Create Contract
          </button>*/}
        </div> 

        {/* =====================================================
            LOADING STATE
            ===================================================== */}

        {loading && (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
            <p
              className="text-gray-500"
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              Loading contracts...
            </p>
          </div>
        )}

        {/* =====================================================
            ERROR STATE
            ===================================================== */}

        {!loading && error && (
          <div className="rounded-xl border border-red-200 bg-white p-5">
            <p
              className="text-red-600"
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              {error}
            </p>

            <button
              type="button"
              onClick={() => void loadContracts()}
              className="mt-3 text-red-600 underline"
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* =====================================================
            EMPTY STATE
            ===================================================== */}

        {!loading && !error && contracts.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
            <h3
              className="text-[#243b53]"
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              No contracts found
            </h3>

            <p
              className="mt-2 text-gray-500"
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              This project does not have any contracts yet.
            </p>

            <button
              type="button"
              onClick={createContract}
              className="
                mt-4
                rounded-md
                bg-[#53538c]
                px-4
                py-2
                text-white
                transition
                hover:bg-[#424270]
                hover:shadow-md
              "
              style={{
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              Create First Contract
            </button>
          </div>
        )}

        {/* =====================================================
            CONTRACT CARDS
            ===================================================== */}

        {!loading && !error && contracts.length > 0 && (
          <div
            className="
              grid
              grid-cols-1
              items-start
              gap-3
              sm:grid-cols-2
              lg:grid-cols-4
              xl:grid-cols-6
            "
          >
            {contracts.map((contract, index) => {
              const headerColor =
                HEADER_COLORS[index % HEADER_COLORS.length];

              return (
                <button
                  key={contract.tenderNo}
                  type="button"
                 /*  onClick={() =>
                    navigate(`/tenders/${contract.tenderNo}`)
                  } */
                 onClick={() => {
                  const contractName =
                    contract.tenderName || "Unnamed Contract";

                  localStorage.setItem("selectedContractName", contractName);

                  navigate(`/tenders/${contract.tenderNo}`, {
                    state: { contractName },
                  });
                }}
                  aria-label={`View contract ${
                    contract.tenderName || contract.tenderNo
                  }`}
                  className="
                    group
                    relative
                    w-full
                    max-w-[240px]
                    justify-self-center
                    overflow-hidden
                    rounded-xl
                    border
                    border-gray-200
                    bg-white
                    text-left
                    shadow-sm
                    transition-all
                    duration-200
                    ease-out
                    hover:-translate-y-1
                    hover:shadow-lg
                    focus:outline-none
                    focus:ring-2
                    focus:ring-[#53538c]
                    focus:ring-offset-2
                  "
                  style={{
                    fontSize: "13px",
                    fontWeight: 400,
                  }}
                >
                  {/* COLORED CARD HEADER */}

                  <div
                    className="
                      relative
                      flex
                      h-[86px]
                      items-end
                      overflow-hidden
                      p-3
                    "
                    style={{
                      backgroundColor: headerColor,
                    }}
                  >
                    {/* DECORATIVE CIRCLE */}

                    <div
                      className="
                        absolute
                        -right-5
                        -top-10
                        h-28
                        w-28
                        rounded-full
                        bg-white
                        opacity-[0.09]
                        transition-transform
                        duration-300
                        group-hover:scale-110
                      "
                    />

                    {/* CONTRACT NAME */}

                    <div className="relative z-10 min-w-0">
                      <p
                        className="truncate text-white"
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                        }}
                      >
                        {contract.tenderName || "Unnamed Contract"}
                      </p>

                      <p
                        className="mt-1 text-white/80"
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                        }}
                      >
                        Contract
                      </p>
                    </div>
                  </div>

                  {/* WHITE CARD FOOTER */}

                  <div
                    className="
                      flex
                      min-h-[48px]
                      items-center
                      justify-between
                      gap-2
                      px-3
                      py-2
                    "
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className="
                          truncate
                          text-[#243b53]
                          transition-colors
                          duration-200
                          group-hover:text-[#53538c]
                        "
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                        }}
                      >
                        {contract.tenderName || "Unnamed Contract"}
                      </p>

                      <p
                        className="mt-1 text-gray-500"
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                        }}
                      >
                        Contract No: {contract.tenderNo}
                      </p>
                    </div>

                    {/* CIRCULAR ARROW */}

                    <span
                      className="
                        flex
                        h-[22px]
                        w-[22px]
                        flex-shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-gray-200
                        text-gray-500
                        transition-all
                        duration-200
                        group-hover:border-[#53538c]
                        group-hover:bg-[#53538c]
                        group-hover:text-white
                      "
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                  </div>

                  {/* HOVER ACCENT */}

                  <div
                    className="
                      absolute
                      bottom-0
                      left-0
                      h-[2px]
                      w-full
                      origin-left
                      scale-x-0
                      bg-[#53538c]
                      transition-transform
                      duration-200
                      group-hover:scale-x-100
                    "
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectContracts;
